/**
 * Connect Bank Button
 *
 * Button to initiate bank account connection via TrueLayer.
 * Opens OAuth flow in popup or redirect mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useInitBankConnection } from '@business-assistant/shared';

interface ConnectBankButtonProps {
  /** Callback when connection completes successfully */
  onSuccess?: (connection: unknown, accounts: unknown[]) => void;
  /** Callback when connection fails */
  onError?: (error: string) => void;
  /** Use popup mode instead of redirect */
  usePopup?: boolean;
  /** Custom button label */
  label?: string;
  /** Full width button */
  fullWidth?: boolean;
  /** Compact mode */
  compact?: boolean;
}

export function ConnectBankButton({
  onSuccess,
  onError,
  usePopup = true,
  label = 'Collega conto bancario',
  fullWidth = false,
  compact = false,
}: ConnectBankButtonProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initBankConnection = useInitBankConnection();

  // Generate unique state for OAuth security
  const generateState = () => {
    return `tl_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  };

  // Handle message from popup callback
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Verify origin
      if (event.origin !== window.location.origin) return;

      const { type, success, error: callbackError, connection, accounts } = event.data || {};

      if (type !== 'truelayer-oauth-callback') return;

      setIsConnecting(false);

      if (success && connection) {
        setError(null);
        onSuccess?.(connection, accounts || []);
      } else if (callbackError) {
        setError(callbackError);
        onError?.(callbackError);
      }
    },
    [onSuccess, onError]
  );

  // Listen for popup messages
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Start connection flow
  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const state = generateState();
      const redirectUri = `${window.location.origin}/oauth/truelayer/callback`;

      const result = await initBankConnection.mutateAsync({
        provider: 'truelayer',
        state,
        redirectUri,
        scopes: ['info', 'accounts', 'balance', 'transactions', 'offline_access'],
      });

      if (!result.success || !result.authUrl) {
        throw new Error(result.errors?.[0]?.message || 'Impossibile avviare la connessione');
      }

      if (usePopup) {
        // Open in popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          result.authUrl,
          'TrueLayer Bank Connection',
          `width=${width},height=${height},left=${left},top=${top},popup=1`
        );

        if (!popup) {
          // Popup blocked, fall back to redirect
          window.location.href = result.authUrl;
        }

        // Check if popup was closed without completing
        const checkPopup = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkPopup);
            // Only set connecting to false if we haven't received a success message
            setTimeout(() => {
              setIsConnecting(false);
            }, 500);
          }
        }, 500);
      } else {
        // Redirect mode
        window.location.href = result.authUrl;
      }
    } catch (err) {
      setIsConnecting(false);
      const message = err instanceof Error ? err.message : 'Errore durante la connessione';
      setError(message);
      onError?.(message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: fullWidth ? '100%' : 'auto' }}>
      <button
        onClick={handleConnect}
        disabled={isConnecting}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: compact ? '8px 16px' : '12px 20px',
          backgroundColor: isConnecting ? '#9ca3af' : '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: compact ? '13px' : '14px',
          fontWeight: 500,
          cursor: isConnecting ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.15s',
          width: fullWidth ? '100%' : 'auto',
        }}
        onMouseEnter={(e) => {
          if (!isConnecting) {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2563eb';
          }
        }}
        onMouseLeave={(e) => {
          if (!isConnecting) {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#3b82f6';
          }
        }}
      >
        {isConnecting ? (
          <>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              style={{ animation: 'spin 1s linear infinite' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>Connessione in corso...</span>
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
              />
            </svg>
            <span>{label}</span>
          </>
        )}
      </button>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#fef2f2',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#dc2626',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10" strokeWidth={2} />
            <line x1="12" y1="8" x2="12" y2="12" strokeWidth={2} />
            <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth={2} />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ConnectBankButton;
